import { queryClient } from '@/common/contexts/react-query/ReactQueryProvider';
import { SalaryService } from '@/common/integrations/menhir/employee/salary/salary.service';
import { MENHIR_ENDPOINTS } from '@/common/integrations/menhir/menhir.endpoints';
import {
  UseMutationOptions,
  UseQueryOptions,
  useMutation,
  useQuery,
} from '@tanstack/react-query';

export const ewaDetailsQueryOptions = {
  queryKey: [MENHIR_ENDPOINTS.EMPLOYEE.SALARY.EWA.DETAILS],
  queryFn: SalaryService.getEWADetails,
};

export const useGetEWADetails = (
  options?: UseQueryOptions<
    Awaited<ReturnType<typeof SalaryService.getEWADetails>>,
    any
  >
) => {
  const { data: details, ...rest } = useQuery({
    ...ewaDetailsQueryOptions,
    ...options,
  });

  return { details, ...rest };
};

export const useSubmitEWARequest = (
  options?: UseMutationOptions<
    Awaited<ReturnType<typeof SalaryService.submitEWARequest>>,
    any,
    Parameters<typeof SalaryService.submitEWARequest>[0]
  >
) => {
  return useMutation({
    mutationFn: SalaryService.submitEWARequest,
    mutationKey: [MENHIR_ENDPOINTS.EMPLOYEE.SALARY.EWA.REQUEST],
    onSuccess: () => {
      queryClient.invalidateQueries([
        MENHIR_ENDPOINTS.EMPLOYEE.SALARY.EWA.DETAILS,
      ]);
    },
    ...options,
  });
};

export const useValidateEWAScheduling = (
  options?: UseMutationOptions<
    Awaited<ReturnType<typeof SalaryService.validateEWAScheduling>>,
    any,
    Parameters<typeof SalaryService.validateEWAScheduling>[0]
  >
) => {
  return useMutation({
    mutationFn: SalaryService.validateEWAScheduling,
    ...options,
  });
};

export const useCreateEWAScheduling = (
  options?: UseMutationOptions<
    Awaited<ReturnType<typeof SalaryService.createEWAScheduling>>,
    any,
    Parameters<typeof SalaryService.createEWAScheduling>[0]
  >
) => {
  return useMutation({
    mutationFn: SalaryService.createEWAScheduling,
    ...options,
  });
};

export const useCancelEWAScheduling = (
  options?: UseMutationOptions<
    Awaited<ReturnType<typeof SalaryService.cancelEWAScheduling>>,
    any
  >
) => {
  return useMutation({
    mutationFn: SalaryService.cancelEWAScheduling,
    onSuccess: () => {
      queryClient.invalidateQueries([
        MENHIR_ENDPOINTS.EMPLOYEE.SALARY.EWA.DETAILS,
      ]);
    },
    ...options,
  });
};

export const useUpdateEWAScheduling = (
  options?: UseMutationOptions<
    Awaited<ReturnType<typeof SalaryService.updateEWAScheduling>>,
    any,
    Parameters<typeof SalaryService.updateEWAScheduling>[0]
  >
) => {
  return useMutation({
    mutationFn: SalaryService.updateEWAScheduling,
    onSuccess: () => {
      queryClient.invalidateQueries([
        MENHIR_ENDPOINTS.EMPLOYEE.SALARY.EWA.DETAILS,
      ]);
    },
    ...options,
  });
};

export const useGetEWACalendar = (
  frequency?: number,
  options?: UseQueryOptions<
    Awaited<ReturnType<typeof SalaryService.getEWACalendar>>,
    any
  >
) => {
  const { data: calendar, ...rest } = useQuery({
    queryKey: [MENHIR_ENDPOINTS.EMPLOYEE.SALARY.EWA.CALENDAR, frequency],
    queryFn: () => SalaryService.getEWACalendar(frequency),
    ...options,
  });

  return { calendar, ...rest };
};
